import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchMainSkin, fetchMainSkins, updateMainSkin, createMainSkin } from "../../actions/mine_craft/mainSkinsActions";

export interface MainSkinInterface {
  url: string;

  id: number;
  title: string;
  body: string;
  search_text: string;
  thumbnail_url: string;
  image_url: string;
  category_id: number | undefined;
  subcategory_id: number | undefined;
  likes_count: number;
  comments_count: number;
  popular_count: number;
  downloads_count: number;
  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: MainSkinInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<MainSkinInterface>();

const slice = createSlice({
  name: 'mainSkins',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchMainSkins.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchMainSkins.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.main_skins;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchMainSkins.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchMainSkin.fulfilled, (state, action) => {
        const mainSkin = Object.values(action.payload.main_skin)[0];
        state.entities[mainSkin.id] = mainSkin;
      })

      // #create
      .addCase(createMainSkin.fulfilled, (state, action) => {
        const mainSkin = Object.values(action.payload.main_skin)[0];
        state.entities[mainSkin.id] = mainSkin;
      })
      .addCase(createMainSkin.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateMainSkin.fulfilled, (state, action) => {
        const mainSkin = Object.values(action.payload.main_skin)[0];
        state.entities[mainSkin.id] = mainSkin;
      })
      .addCase(updateMainSkin.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectMainSkinById,
  selectIds: selectMainSkinIds,
  selectEntities: selectMainSkinEntities,
  selectAll: selectAllMainSkins,
  selectTotal: selectTotalMainSkins
} = adaptor.getSelectors<RootState>(state => state.mineCraft.mainSkins);
